// Singleton WASM bridge. Spawns one Worker for the page and exposes a
// typed runCommand(...) that returns a Promise<string>.

import type { Resume } from './schema';

type Pending = {
  resolve: (output: string) => void;
  reject: (err: Error) => void;
};

let workerPromise: Promise<Worker> | null = null;
const pending = new Map<string, Pending>();

function spawnWorker(): Promise<Worker> {
  // Classic (non-module) worker — `importScripts` (used inside the
  // worker to load wasm_exec.js) is only available in classic workers.
  const worker = new Worker(new URL('./wasm.worker.ts', import.meta.url));

  return new Promise<Worker>((resolve, reject) => {
    const ready = (event: MessageEvent) => {
      if (event.data?.type === 'ready') {
        worker.removeEventListener('message', ready);
        resolve(worker);
      } else if (event.data?.type === 'boot-error') {
        worker.removeEventListener('message', ready);
        reject(new Error(event.data.error ?? 'the WebAssembly engine failed to start'));
      }
    };
    worker.addEventListener('message', ready);
    worker.addEventListener('error', (e) => reject(new Error(e.message)));

    worker.addEventListener('message', (event: MessageEvent) => {
      const { type, requestId, output, error } = event.data ?? {};
      if (type !== 'response' || !requestId) return;
      const cb = pending.get(requestId);
      if (!cb) return;
      pending.delete(requestId);
      if (error) cb.reject(new Error(error));
      else cb.resolve(output ?? '');
    });
  });
}

export async function runCommand(command: string): Promise<string> {
  workerPromise ??= spawnWorker();
  const worker = await workerPromise;

  return new Promise<string>((resolve, reject) => {
    const requestId = crypto.randomUUID();
    pending.set(requestId, { resolve, reject });
    worker.postMessage({ type: 'command', requestId, command });
  });
}

/**
 * Asks the Go side to produce completion candidates for the given
 * partial command line. Returns the candidate list (descriptions
 * stripped). Empty array when nothing matches.
 */
export async function completeLine(line: string): Promise<string[]> {
  workerPromise ??= spawnWorker();
  const worker = await workerPromise;

  const raw = await new Promise<string>((resolve, reject) => {
    const requestId = crypto.randomUUID();
    pending.set(requestId, { resolve, reject });
    worker.postMessage({ type: 'complete', requestId, line });
  });

  try {
    const parsed = JSON.parse(raw) as { candidates?: string[] };
    return parsed.candidates ?? [];
  } catch {
    return [];
  }
}

/**
 * The single command that produces the canonical JSON Resume document. The
 * web résumé, the home page, the PDF and the ATS output all go through it, so
 * there is exactly one source of résumé facts.
 */
export const RESUME_QUERY = 'kubectl get resume main-resume -o json';

/**
 * Fetches the resume.json by running the same kubectl-style command the
 * user would type in the terminal, then parses the wrapping array.
 */
export async function fetchResume(): Promise<Resume> {
  const raw = await runCommand(RESUME_QUERY);
  const parsed = JSON.parse(raw) as Resume[] | Resume;
  return Array.isArray(parsed) ? parsed[0] : parsed;
}
